import PropTypes from 'prop-types';
import React from 'react';

import { breakpoints } from 'javascripts/shared/utils/breakpoint_utils';
import { bubEvents } from 'javascripts/shared/events/bub_events';
import { graphQLHelpers } from 'javascripts/shared/graphql/helpers';

import { useDimensions } from 'javascripts/hooks/use-dimensions';

import { BookCard } from 'javascripts/components/book_card';
import { BookCardPlaceholder } from 'javascripts/components/placeholders/book_card_placeholder';
import { CarouselWithGraphQL } from 'javascripts/components/carousel_with_graphql';

const DEFAULT_CAROUSEL_SLIDE_COUNT = 5;

export function BookCardCarouselWithGraphQL(props) {
  const [carouselRef, { width }] = useDimensions(props.useResizable, ['resize']);

  function renderCarouselItems(bookList, trackingCategory) {
    return bookList.map((book, index) => (
      <div key={`${trackingCategory} ${book.id}`} aria-label={book.displayTitle}>
        <BookCard
          audiobook={book}
          useSmallText={props.useSmallText || (props.useResizable && (!width || width < breakpoints.lg))}
          trackingCategory={trackingCategory}
          trackingProperties={{
            ...props.trackingProperties,
            carousel_horizontal_position: bubEvents.trackingIndex(index),
          }}
          coverSize='medium'
          showCoverButtons={props.showCoverButtons}
          showStarRating={props.showStarRatings}
        />
      </div>
    ));
  }

  function renderResponse(graphQLResponse, trackingCategory) {
    if (graphQLResponse.loading) {
      return [];
    }

    if (!_.isEmpty(graphQLResponse.errors)) {
      throw graphQLResponse.errors[0];
    }

    const responseItems = props.fetchResponseItems(graphQLResponse);

    if (props.responseDataType === 'promotion') {
      return renderCarouselItems(graphQLHelpers.reshapePromotionsResponse(responseItems), trackingCategory);
    }

    return renderCarouselItems(responseItems, trackingCategory);
  }

  function trackLinkClick() {
    bubEvents.track(props.trackingCategory, 'Clicked link', {
      label: props.linkText,
      ...props.trackingProperties,
    });
  }

  return (
    <div ref={carouselRef} className={props.carouselWrapperClass} data-testid='book-card-carousel-with-graphql'>
      <CarouselWithGraphQL
        query={props.query}
        queryParams={props.queryParams}
        title={props.title}
        renderResponseFunction={renderResponse}
        trackingCategory={props.trackingCategory}
        slideSize={props.slideSize || DEFAULT_CAROUSEL_SLIDE_COUNT}
        arrowTrackingCategory={props.arrowTrackingCategory}
        emptyMessage={props.emptyMessage}
        hideArrows={props.hideArrows}
        hideEmptyCarousel={props.hideEmptyCarousel}
        linkHref={props.linkHref}
        minSize={props.minSize}
        titleElement={props.titleElement}
        linkText={props.linkText}
        linkTracker={trackLinkClick}
        onDataLoad={props.onDataLoad}
        placeholderItem={
          <BookCardPlaceholder
            showStarRating={props.showStarRatings}
            showTimeLeftPlaceholder={props.showTimeLeftPlaceholder}
            useSmallTextPlaceholders={props.useSmallText || (props.useResizable && (!width || width < breakpoints.lg))}
          />
        }
        useSmallArrows={props.useSmallText}
      />
    </div>
  );
}

BookCardCarouselWithGraphQL.propTypes = {
  carouselWrapperClass: PropTypes.string,
  fetchResponseItems: PropTypes.func.isRequired,
  query: PropTypes.string.isRequired,
  queryParams: PropTypes.object,
  responseDataType: PropTypes.oneOf(['audiobook', 'promotion']).isRequired,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  trackingCategory: PropTypes.string.isRequired,
  slideSize: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7]),
  minSize: PropTypes.number,
  arrowTrackingCategory: PropTypes.string,
  emptyMessage: PropTypes.string,
  hideArrows: PropTypes.bool,
  hideEmptyCarousel: PropTypes.bool,
  linkHref: PropTypes.string,
  linkText: PropTypes.string,
  titleElement: PropTypes.oneOf(['div', 'h2', 'h3']),
  onDataLoad: PropTypes.func,
  useSmallText: PropTypes.bool,
  trackingProperties: PropTypes.object,
  showCoverButtons: PropTypes.bool,
  showStarRatings: PropTypes.bool,
  showTimeLeftPlaceholder: PropTypes.bool,
  useResizable: PropTypes.bool,
};

BookCardCarouselWithGraphQL.defaultProps = {
  minSize: 0,
  queryParams: {},
  hideEmptyCarousel: false,
  onDataLoad: _.noop,
  useSmallText: false,
  trackingProperties: {},
  showStarRatings: false,
  showTimeLeftPlaceholder: true,
  useResizable: false,
};
